import {AnchoredOverlay, Link as PrimerLink, Heading, Truncate} from '@primer/react'
import {TopicTag} from '@primer/react/experimental'
import {useQueryClient, type QueryKey} from '@github-ui/react-query'
import {Link} from '@github-ui/react-router'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import {useClickAnalytics} from '@github-ui/use-analytics'
import {clsx} from 'clsx'
import {useCallback, useState} from 'react'
import {
  AccessibilityInsetIcon,
  BookIcon,
  CodeOfConductIcon,
  EyeIcon,
  GearIcon,
  LawIcon,
  LinkIcon,
  LogIcon,
  NoteIcon,
  PeopleIcon,
  PulseIcon,
  RepoForkedIcon,
  ShieldLockIcon,
  StarIcon,
} from '@primer/octicons-react'
import type {SidebarAboutProps} from '../types'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {SidebarSection} from './SidebarSection'
import {SidebarLink} from './SidebarLink'
import {SidebarResourceLink} from './SidebarResourceLink'
import {SidebarCitation} from './SidebarCitation'
import {EditRepositoryDetailsDialog} from './EditRepositoryDetailsDialog'
import {socialCount, safeHref, truncateDescription, MAX_DESCRIPTION_LENGTH} from '../utils'
import styles from './SidebarAbout.module.css'

/**
 * Matches every cached `/_sidebar/about` query for the repository. Route queries key it as
 * `[appName, routeId, routePath, 'sidebarAbout', deps]` and page-level queries as
 * `[owner, repo, 'sidebarAbout', deps]`, so match on the shared query name and deps.
 */
function isSidebarAboutQuery(queryKey: QueryKey, pathname: string) {
  const queryDeps = queryKey.at(-1)
  return (
    queryKey.at(-2) === 'sidebarAbout' &&
    typeof queryDeps === 'object' &&
    queryDeps !== null &&
    'pathname' in queryDeps &&
    queryDeps.pathname === pathname
  )
}

/** Renders the About section of the repository sidebar. */
export function SidebarAbout({
  description,
  isOrg,
  formattedDescription,
  website,
  topics,
  hasReadme,
  licenses,
  hasCodeOfConduct,
  hasContributing,
  hasSecurityPolicy,
  hasAccessibility,
  hasCitation,
  showInsights,
  canOpenStargazersAndWatchers,
  stargazerCount,
  watcherCount,
  forksCount,
  stargazersPath,
  watchersPath,
  forkNetworkPath,
  activityPath,
  customPropertiesPath,
  auditLogPath,
  reportPath,
  ownerLogin,
  repoName,
  canEditMetadata,
  sections,
  pagesUrl,
  className,
}: SidebarAboutProps & React.HTMLAttributes<HTMLDivElement>) {
  const {sendClickAnalyticsEvent} = useClickAnalytics()
  const queryClient = useQueryClient()
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const invalidateAboutQuery = useCallback(() => {
    const aboutPathname = `/${ownerLogin}/${repoName}/_sidebar/about`
    return queryClient.invalidateQueries({predicate: ({queryKey}) => isSidebarAboutQuery(queryKey, aboutPathname)})
  }, [ownerLogin, queryClient, repoName])
  const hasDescription = !!description
  const hasWebsite = !!website
  const safeSite = website ? safeHref(website) : null
  const hasTopics = topics && topics.length > 0
  const showNoDescriptionMessage = !hasDescription && !hasWebsite

  return (
    <SidebarSection
      title={
        canEditMetadata ? (
          <span className={styles.aboutHeading}>
            <span>About</span>
            <button
              type="button"
              className={styles.editButton}
              onClick={() => setEditDialogOpen(true)}
              aria-label="Edit repository metadata"
            >
              <GearIcon />
            </button>
          </span>
        ) : (
          'About'
        )
      }
      className={clsx(className, 'border-0')}
    >
      {canEditMetadata && sections && editDialogOpen && (
        <EditRepositoryDetailsDialog
          onClose={() => setEditDialogOpen(false)}
          onSaved={invalidateAboutQuery}
          ownerLogin={ownerLogin}
          repoName={repoName}
          description={description}
          website={website}
          topics={topics}
          showDeploymentsSection={sections.deployments !== false}
          showReleasesSection={sections.releases !== false}
          showPackagesSection={sections.packages}
          pagesUrl={pagesUrl}
        />
      )}
      {showNoDescriptionMessage && (
        <div className={styles.noDescription}>
          {hasTopics ? (
            <span>No description or website provided.</span>
          ) : (
            <span>No description, website, or topics provided.</span>
          )}
        </div>
      )}

      {hasDescription && <Description text={description} html={formattedDescription} />}
      {hasWebsite && safeSite && (
        <div className={styles.websiteRow}>
          {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
          <LinkIcon className="shrink-0" />
          <Truncate as="span" className={styles.websiteLink} title={website} maxWidth="none">
            <PrimerLink href={safeSite} target="_blank" rel="noopener noreferrer nofollow" className="text-bold">
              {website.replace(/^https?:\/\//, '')}
            </PrimerLink>
          </Truncate>
        </div>
      )}

      {hasTopics && (
        <>
          <Heading as="h3" className="sr-only">
            <span>Topics</span>
          </Heading>
          <TopicTag.Group>
            {topics.map(topic => (
              <TopicTag
                key={`topic-${topic.name}`}
                as={Link}
                to={`/topics/${encodeURIComponent(topic.name)}`}
                reloadDocument
                onClick={() =>
                  sendClickAnalyticsEvent({
                    category: 'REPOSITORY_OVERVIEW',
                    action: 'click',
                    label: 'location:sidebar;section:topics',
                  })
                }
              >
                {topic.name}
              </TopicTag>
            ))}
          </TopicTag.Group>
        </>
      )}

      {hasReadme && (
        <SidebarResourceLink
          icon={BookIcon}
          label="Readme"
          // uses href for hash links to trigger `hashchange` events
          href="#readme-ov-file"
          analyticsLabel="file:readme"
          srHeading="Resources"
        />
      )}

      {licenses && licenses.length > 1 && (
        <MultiLicenseLinks licenses={licenses} ownerLogin={ownerLogin} repoName={repoName} />
      )}

      {licenses && licenses.length === 1 && licenses[0] && (
        <SidebarResourceLink
          icon={LawIcon}
          label={formatLicenseLabel(licenses[0].name)}
          href={`#${licenseAnchor(licenses[0])}`}
          analyticsLabel="file:license"
        />
      )}

      {hasCodeOfConduct && (
        <SidebarResourceLink
          icon={CodeOfConductIcon}
          label="Code of conduct"
          to="#coc-ov-file"
          analyticsLabel="file:code of conduct"
          srHeading="Code of conduct"
        />
      )}

      {hasContributing && (
        <SidebarResourceLink
          icon={PeopleIcon}
          label="Contributing"
          href="#contributing-ov-file"
          analyticsLabel="file:contributing"
          srHeading="Contributing"
        />
      )}

      {hasSecurityPolicy && (
        <SidebarResourceLink
          icon={ShieldLockIcon}
          label="Security policy"
          href="#security-ov-file"
          analyticsLabel="file:security policy"
          srHeading="Security policy"
        />
      )}

      {hasAccessibility && (
        <SidebarResourceLink
          icon={AccessibilityInsetIcon}
          label="Accessibility"
          href="#accessibility-ov-file"
          analyticsLabel="file:accessibility"
          srHeading="Accessibility"
        />
      )}

      {hasCitation && (
        <div className="mt-2">
          <SidebarCitation ownerLogin={ownerLogin} repoName={repoName} />
        </div>
      )}

      {showInsights && (
        <div className={styles.insights}>
          <div className={styles.insightLinks}>
            {activityPath && (
              <SidebarResourceLink
                icon={PulseIcon}
                label="Activity"
                to={activityPath}
                analyticsLabel="section:activity"
              />
            )}

            {customPropertiesPath && isOrg && (
              <SidebarResourceLink icon={NoteIcon} label="Custom properties" to={customPropertiesPath} />
            )}
          </div>

          <div className={styles.insightStats}>
            <Heading as="h3" className="sr-only">
              <span>Stars</span>
            </Heading>
            <div className={clsx('mt-2', styles.insightItem)}>
              {canOpenStargazersAndWatchers ? (
                <SidebarLink to={stargazersPath} muted analyticsLabel="section:stars">
                  <StarIcon className="mr-2" />
                  <span>
                    <strong>{socialCount(stargazerCount)}</strong>
                  </span>
                  <span>{` star${stargazerCount === 1 ? '' : 's'}`}</span>
                </SidebarLink>
              ) : (
                <span className={styles.socialStat}>
                  <StarIcon className="mr-2" />
                  <strong>{socialCount(stargazerCount)}</strong>
                  {` star${stargazerCount === 1 ? '' : 's'}`}
                </span>
              )}
            </div>

            <Heading as="h3" className="sr-only">
              <span>Watchers</span>
            </Heading>
            <div className={clsx('mt-2', styles.insightItem)}>
              {canOpenStargazersAndWatchers ? (
                <SidebarLink to={watchersPath} muted analyticsLabel="section:watchers">
                  <EyeIcon className="mr-2" />
                  <span>
                    <strong>{socialCount(watcherCount)}</strong>
                  </span>
                  <span>{' watching'}</span>
                </SidebarLink>
              ) : (
                <span className={styles.socialStat}>
                  <EyeIcon className="mr-2" />
                  <strong>{socialCount(watcherCount)}</strong>
                  {' watching'}
                </span>
              )}
            </div>

            <Heading as="h3" className="sr-only">
              <span>Forks</span>
            </Heading>
            <div className={clsx('mt-2', styles.insightItem)}>
              <SidebarLink to={forkNetworkPath} muted analyticsLabel="section:forks">
                <RepoForkedIcon className="mr-2" />
                <span>
                  <strong>{socialCount(forksCount)}</strong>
                </span>
                <span>{` fork${forksCount === 1 ? '' : 's'}`}</span>
              </SidebarLink>
            </div>
          </div>
        </div>
      )}

      {auditLogPath && <SidebarResourceLink icon={LogIcon} label="Audit log" to={auditLogPath} />}

      {reportPath && (
        <div className="mt-2">
          <SidebarLink to={reportPath} reloadDocument muted analyticsLabel="section:report">
            <span>Report repository</span>
          </SidebarLink>
        </div>
      )}
    </SidebarSection>
  )
}

function Description({text, html}: {text?: string; html?: SafeHTMLString}) {
  // When a description exceeds the limit (legacy repos predating the 350-char cap),
  // render truncated plain text so desktop matches mobile. HTML formatting (auto-links,
  // emoji) is preserved for descriptions within the limit.
  const overLimit = text !== undefined && text.length > MAX_DESCRIPTION_LENGTH
  if (html && !overLimit) {
    return <VerifiedHTMLText as="p" className={styles.description} html={html} />
  }
  return <p className={styles.description}>{text ? truncateDescription(text) : text}</p>
}

function licenseAnchor(lic: {name: string; index?: number}): string {
  const idx = lic.index ?? 1
  return `${lic.name}-${idx}-ov-file`
}

function formatLicenseLabel(name: string): string {
  return name.toLowerCase().includes('license') ? name : `${name} license`
}

function multiLicensesSummary(licenses: Array<{name: string}>): string {
  if (licenses.length === 2) {
    return `${licenses.map(l => l.name).join(', ')} licenses found`
  }
  const first = licenses[0]
  const remaining = licenses.length - 1
  return `${first?.name ?? 'Unknown'} and ${remaining} other license${remaining === 1 ? '' : 's'} found`
}

function MultiLicenseLinks({
  licenses,
  ownerLogin,
  repoName,
}: {
  licenses: Array<{name: string; index?: number; path?: string; refName?: string}>
  ownerLogin: string
  repoName: string
}) {
  const [open, setOpen] = useState(false)

  const handleOpen = useCallback(() => setOpen(true), [])
  const handleClose = useCallback(() => setOpen(false), [])

  return (
    <div className="mt-2">
      <AnchoredOverlay
        open={open}
        onOpen={handleOpen}
        onClose={handleClose}
        width="medium"
        side="outside-bottom"
        align="end"
        overlayProps={{
          role: 'dialog',
          'aria-label': 'Licenses found',
          style: {minWidth: '300px'},
        }}
        renderAnchor={props => (
          <button {...props} type="button" className={styles.licensesTrigger}>
            <LawIcon className="mr-2" />
            <span>{multiLicensesSummary(licenses)}</span>
            <span className={styles.licensesCaret} />
          </button>
        )}
      >
        <div>
          <Heading as="h3" className={styles.licensesDialogTitle}>
            Licenses found
          </Heading>
          <ul className={styles.licensesList}>
            {licenses.map(lic => {
              const href = lic.path
                ? `/${ownerLogin}/${repoName}/blob/${lic.refName ?? 'HEAD'}/${lic.path}`
                : `#${licenseAnchor(lic)}`
              return (
                <li key={`lic-${lic.name}-${lic.index}`}>
                  <SidebarLink to={href} className={styles.licensesItem} onClick={handleClose}>
                    <strong>{lic.name}</strong>
                    {lic.path && <span className={styles.licensesFilepath}>{lic.path}</span>}
                  </SidebarLink>
                </li>
              )
            })}
          </ul>
        </div>
      </AnchoredOverlay>
    </div>
  )
}
